// src/components/CrystalsHeader.jsx

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import {
    setLink,
    syncTelegramMeta,
    getPromoterLinkDisplay,
    getPromoterDisplayName,
    getPromoterAvatarSrc,
    normalizeTelegramLink,
    isTelegramPromoterLink,
    TELEGRAM_LINK_ONLY_MESSAGE,
    TELEGRAM_NOT_FOUND_MESSAGE,
} from '../../../store/slices/promoTelegramSlice';
import { PromoSnackbar, usePromoSnackbar } from './PromoSnackbar';
import getIcon from '../../../utils/Icon';
import CoinBadge from '../../coin_badge/CoinBadge';
import CrystalsApi from '../../../api/CrystalsApi';
import feeApi from '../../../api/feeApi';
import { useKeyboard } from '../../Keyboard';
import { getCrystalCategoryPlayers, getCrystalCategoryBudget } from '../../../utils/participantLimits';
import { buildCrystalPositions } from '../../../utils/crystalPositions';
import { getCrystalRarityTier } from '../../../data/crystalRarityTiers';
import { addRecentLink, getRecentLinks, PROMOTER_TG_LINK_RECENT_KEY } from '../../../utils/keyboardRecentLinks';
import Lottie from 'lottie-react';
import swipeAnim from '../../../assets/lottie/swipe.json';

// Мемоизированные компоненты вне основного компонента
const TimeSquares = React.memo(({ value }) => (
    <div className="w-[24px] h-[24px] text-[12px] font-roboto text-white flex items-center justify-center bg-white/15 rounded-[4px]">
        {value}
    </div>
));

const Time = React.memo(({ days = 7 }) => (
    <div className=" h-[44px] flex items-center justify-between">
        {/* <span className="text-[14px] font-semibold text-[#D8D8D8]">Promo by</span> */}
        <div className=" flex justify-end items-center gap-1">
            <TimeSquares value={days} /><span className="text-[12px] font-roboto">D</span>
            {/* <TimeSquares value={0} /><span className="text-[12px] font-roboto">H</span> */}
            {/* <TimeSquares value={0} /><span className="text-[12px] font-roboto">M</span> */}
        </div>
    </div>
));

const toRoman = (num) => {
    const romanNumerals = [
        { value: 10, symbol: 'X' },
        { value: 9, symbol: 'IX' },
        { value: 5, symbol: 'V' },
        { value: 4, symbol: 'IV' },
        { value: 1, symbol: 'I' },
    ];

    let result = '';
    for (const { value, symbol } of romanNumerals) {
        while (num >= value) {
            result += symbol;
            num -= value;
        }
    }
    return result;
};

const Crystal = React.memo(({ position, count, src, onClick }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    return (
        <div
            className={`w-[40px] h-[66px] flex flex-col items-center justify-center ${onClick ? 'cursor-pointer active:scale-95 transition-transform' : ''}`}
            onClick={onClick}
        >
            <span className="text-[12px] font-roboto text-[#959595]">{toRoman(position)}</span>
            <div className="w-[33px] h-[33px] flex items-center justify-center">
                {!imageLoaded && !imageError && (
                    <div className="w-[33px] h-[33px] rounded-full bg-gray-300 animate-pulse" />
                )}
                {imageError ? (
                    <div className="w-[33px] h-[33px] rounded-full bg-gray-600 flex items-center justify-center">
                        <span className="text-[10px] text-white">?</span>
                    </div>
                ) : (
                    <img
                        src={src}
                        alt="crystal"
                        className={`w-[33px] h-[33px] rounded-full ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => {
                            setImageError(true);
                            setImageLoaded(true);
                        }}
                    />
                )}
            </div>
            <span className="text-[12px] font-roboto text-white">{count}</span>
        </div>
    );
});

const ParticipantsValueRect = React.memo(({ title, count, percentage, onClick }) => (
    <div className={`w-[84px] flex flex-col items-center justify-center gap-1 ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
        <span className="text-[#D8D8D8] text-[12px]">{title}</span>
        <div className={`w-full h-6 flex items-center justify-center font-roboto text-[14px] rounded-[40px] border
            ${title === 'winners' ? 'border-white/15' : 'border-white/15'}
            ${!percentage ? ' text-black' : ' text-white'}
            ${onClick ? 'active:scale-95 transition-transform' : ''}`}>
            {count}
        </div>
        <span className="text-[#959595] text-[12px] font-roboto">
            {percentage ? `${percentage}%` : '\u00A0'}
        </span>
    </div>
));

const IMAGE_BASE_URL = 'https://api.duotetra.quest/uploads/gems/';

const formatChancePercent = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '0';
    return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '');
};

export default function CrystalsHeader() {
    const dispatch = useDispatch();
    const { link, data, loading: tgLoading } = useSelector(s => s.promoTelegram);
    const { items: crystals, selectedId } = useSelector(s => s.crystals);
    const selectedCategory = crystals.find(cat => cat.id === selectedId);
    const { openKeyboard } = useKeyboard();
    const LINK_PLACEHOLDER = 'Promoter TG link';
    const LEGACY_LINK_PLACEHOLDER = 'Promoter link';

    const normalizePromoterLink = useCallback((value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return '';
        const lower = trimmed.toLowerCase();
        if (lower === LINK_PLACEHOLDER.toLowerCase() || lower === LEGACY_LINK_PLACEHOLDER.toLowerCase()) return '';
        return trimmed;
    }, []);

    const promoterLinkDisplay = useMemo(() => {
        if (!link || link === LINK_PLACEHOLDER || link === LEGACY_LINK_PLACEHOLDER) {
            return LINK_PLACEHOLDER;
        }
        return getPromoterLinkDisplay(link, data, LINK_PLACEHOLDER);
    }, [link, data]);

    const hasResolvedPromoterLink = promoterLinkDisplay !== LINK_PLACEHOLDER;

    // Bonus mode (1st Time Free)
    const bonusPosition = useSelector(s => s.promoForm?.bonusPosition);
    const isBonusMode = !!bonusPosition;

    const [activePresetModal, setActivePresetModal] = useState(null);
    const [showPromoterTooltip, setShowPromoterTooltip] = useState(false);
    const [showPrizePoolTooltip, setShowPrizePoolTooltip] = useState(false);
    const { snackbar, snackbarAnimation, showSnackbar, closeSnackbar } = usePromoSnackbar();
    const promoterRef = useRef(null);
    const prizePoolRef = useRef(null);

    useEffect(() => {
        if (!showPromoterTooltip) return;

        const handleClickOutside = (event) => {
            if (promoterRef.current && !promoterRef.current.contains(event.target)) {
                setShowPromoterTooltip(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showPromoterTooltip]);

    useEffect(() => {
        if (!showPrizePoolTooltip) return;

        const handleClickOutside = (event) => {
            if (prizePoolRef.current && !prizePoolRef.current.contains(event.target)) {
                setShowPrizePoolTooltip(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showPrizePoolTooltip]);

    // 1) Загружаем все GemType
    const [gemTypes, setGemTypes] = useState([]);
    useEffect(() => {
        CrystalsApi.getAllGems()
            .then(setGemTypes)
            .catch(err => console.error('Ошибка загрузки типов гемов:', err));
    }, []);

    // 2) Строим массив URL-ов frontImage по реверснутым position
    const [positionIcons, setPositionIcons] = useState([]);
    useEffect(() => {
        if (!selectedCategory || gemTypes.length === 0) {
            setPositionIcons([]);
            return;
        }
        const count = selectedCategory.number;
        const icons = Array.from({ length: count }, (_, i) => {
            const pos = count - i; // реверс: самая большая позиция first
            const gem = gemTypes.find(g => g.position === pos);
            return gem
                ? IMAGE_BASE_URL + gem.frontImage
                : getIcon('diamond');
        });
        setPositionIcons(icons);
    }, [selectedCategory, gemTypes]);

    // 3) Рассчитываем комиссию (fee) по minParticipantsCount
    const [fee, setFee] = useState(null);
    const [feeLoading, setFeeLoading] = useState(false);

    useEffect(() => {
        if (!selectedCategory) {
            setFee(null);
            return;
        }
        const count = getCrystalCategoryPlayers(selectedCategory);
        setFeeLoading(true);

        (async () => {
            try {
                const allFees = await feeApi.feeLevelsApi.getAll();
                if (!allFees?.length) {
                    setFee(null);
                    return;
                }
                // только для кристалов
                const filtered = allFees
                    .filter(f => f.onlyGem)
                    .sort((a, b) => a.max - b.max);

                let resultFee = filtered[0]?.fee ?? 0;
                for (let i = 1; i < filtered.length; i++) {
                    if (count <= filtered[i].max) {
                        const prev = filtered[i - 1];
                        const curr = filtered[i];
                        const inc = (curr.fee - prev.fee) / (curr.max - prev.max);
                        resultFee = prev.fee + inc * (count - prev.max);
                        break;
                    }
                }
                if (count > filtered[filtered.length - 1].max) {
                    resultFee = filtered[filtered.length - 1].fee;
                }
                setFee(resultFee.toFixed(2));
            } catch (e) {
                console.error('Ошибка расчёта fee:', e);
                setFee(null);
            } finally {
                setFeeLoading(false);
            }
        })();
    }, [selectedCategory]);

    // Telegram-инпут с кастомной клавиатурой
    const handleOpenKeyboard = useCallback(() => {
        if (tgLoading) return;

        const username = window.Telegram?.WebApp?.initDataUnsafe?.user?.username;
        const selfLink = username ? `https://t.me/${username}` : '';
        const recent = getRecentLinks(PROMOTER_TG_LINK_RECENT_KEY);
        const suggestions = [selfLink, ...recent]
            .map((s) => String(s || '').trim())
            .filter(Boolean)
            .filter((link, idx, arr) => arr.indexOf(link) === idx);
        
        openKeyboard({
            type: 'qwerty',
            value: '',
            label: 'Promoter TG link',
            suggestions,
            suggestionsLabel: '',
            onChange: (finalValue) => {
                const normalizedValue = normalizePromoterLink(finalValue);
                if (!normalizedValue) return;
                if (!isTelegramPromoterLink(normalizedValue)) {
                    showSnackbar(TELEGRAM_LINK_ONLY_MESSAGE, 'error');
                    dispatch(setLink(''));
                    return;
                }
                const telegramLink = normalizeTelegramLink(normalizedValue);
                dispatch(setLink(telegramLink));
                addRecentLink(PROMOTER_TG_LINK_RECENT_KEY, telegramLink);
            },
            onSubmit: async (finalValue) => {
                const normalizedValue = normalizePromoterLink(finalValue);
                if (!normalizedValue || !isTelegramPromoterLink(normalizedValue)) return;
                const telegramLink = normalizeTelegramLink(normalizedValue);
                addRecentLink(PROMOTER_TG_LINK_RECENT_KEY, telegramLink);
                try {
                    await dispatch(syncTelegramMeta(telegramLink)).unwrap();
                } catch (err) {
                    dispatch(setLink(''));
                    showSnackbar(
                        typeof err === 'string' ? err : TELEGRAM_NOT_FOUND_MESSAGE,
                        'error'
                    );
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, dispatch, tgLoading, normalizePromoterLink, showSnackbar]);

    const CrystalDetailModal = ({ modalData, onClose }) => {
        const [imageLoaded, setImageLoaded] = useState(false);
        const [imageError, setImageError] = useState(false);
        const presets = selectedCategory?.presets || [];
        const [slideIndex, setSlideIndex] = useState(0);
        const [direction, setDirection] = useState(0);
        const [showSwipeHint, setShowSwipeHint] = useState(false);

        useEffect(() => {
            if (!modalData) return;
            const list = selectedCategory?.presets || [];
            const idx = list.findIndex((p) => p === modalData.preset);
            setSlideIndex(idx >= 0 ? idx : modalData.iconId ?? 0);
            setDirection(0);
            setShowSwipeHint(list.length > 1);

            if (list.length <= 1) return undefined;

            const timer = setTimeout(() => setShowSwipeHint(false), 2500);
            return () => clearTimeout(timer);
        }, [modalData]);

        useEffect(() => {
            setImageLoaded(false);
            setImageError(false);
        }, [slideIndex]);

        if (!modalData || presets.length === 0) return null;

        const reversedOrder = Boolean(modalData.reversedOrder);
        const preset = presets[slideIndex];
        const iconId = slideIndex;
        const rewardTier = getCrystalRarityTier(slideIndex, presets.length);
        const chancePercent = formatChancePercent(preset?.budget_percent ?? 0);

        const goToSlide = (nextIndex) => {
            if (presets.length <= 1) return;

            let target = nextIndex;
            if (target < 0) target = presets.length - 1;
            if (target >= presets.length) target = 0;
            if (target === slideIndex) return;

            setShowSwipeHint(false);
            let dir;
            if (slideIndex === presets.length - 1 && target === 0) {
                dir = 1;
            } else if (slideIndex === 0 && target === presets.length - 1) {
                dir = -1;
            } else {
                dir = target > slideIndex ? 1 : -1;
            }
            if (reversedOrder) dir = -dir;
            setDirection(dir);
            setSlideIndex(target);
        };

        const goNext = () => goToSlide(reversedOrder ? slideIndex - 1 : slideIndex + 1);
        const goPrev = () => goToSlide(reversedOrder ? slideIndex + 1 : slideIndex - 1);

        const slideVariants = {
            enter: (dir) => ({
                x: dir === 0 ? 0 : dir > 0 ? 80 : -80,
                opacity: dir === 0 ? 1 : 0,
            }),
            center: { x: 0, opacity: 1 },
            exit: (dir) => ({
                x: dir > 0 ? -80 : 80,
                opacity: 0,
            }),
        };

        return (
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="absolute inset-0 bg-[#232323]/90 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                />
                <motion.div
                    className="relative z-10 mb-10 w-full max-w-[300px] h-[300px] px-3 overflow-hidden"
                    initial={{ opacity: 0, scale: 0.92, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 16 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <AnimatePresence initial={false} custom={direction} mode="wait">
                        <motion.div
                            key={slideIndex}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.15}
                            onDragEnd={(_, info) => {
                                if (info.offset.x < -30 || info.velocity.x < -30) {
                                    goNext();
                                } else if (info.offset.x > 30 || info.velocity.x > 30) {
                                    goPrev();
                                }
                            }}
                            className="relative flex flex-col items-center touch-pan-y cursor-grab active:cursor-grabbing"
                        >
                            <div className="absolute top-[110px] right-0 left-0 m-auto w-[220px] h-[170px] rounded-lg bg-gradient-to-b from-[#353232] to-transparent" />
                            <div className="relative flex flex-col items-center justify-center pt-4 z-10">
                                <span className="text-white text-[14px] font-semibold translate-y-4">
                                    {toRoman(slideIndex + 1)}
                                </span>
                                <div className="relative w-[140px] h-[140px]">
                                    {!imageLoaded && !imageError && (
                                        <div className="w-[140px] h-[140px] rounded-full bg-gray-300/20 animate-pulse absolute inset-0" />
                                    )}
                                    {imageError ? (
                                        <div className="w-[140px] h-[140px] rounded-full bg-gray-600 flex items-center justify-center">
                                            <span className="text-white text-[12px]">?</span>
                                        </div>
                                    ) : (
                                        <img
                                            width={140}
                                            height={140}
                                            src={positionIcons[iconId] || ''}
                                            alt=""
                                            draggable={false}
                                            className={`w-[140px] h-[140px] rounded-full object-contain transition-opacity duration-200 ${
                                                imageLoaded ? 'opacity-100' : 'opacity-0'
                                            }`}
                                            onLoad={() => setImageLoaded(true)}
                                            onError={() => setImageError(true)}
                                        />
                                    )}
                                </div>
                                <span className="text-[16px] -translate-y-6 font-medium text-white capitalize">
                                    {rewardTier.name} crystal
                                </span>
                                <span className="text-[#959595] text-[12px] ">
                                    {chancePercent}% chance
                                </span>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {presets.length > 1 && (
                        <motion.div
                            className="flex justify-center pointer-events-none translate-y-5"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: showSwipeHint ? 1 : 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Lottie
                                animationData={swipeAnim}
                                loop
                                style={{ width: 72, height: 72 }}
                            />
                        </motion.div>
                    )}
                </motion.div>
            </motion.div>
        );
    };

    // Мемоизированная линия кристаллов
    const crystalsLine = useMemo(() => {
        if (!selectedCategory) return null;
        // Кол-во кристаллов по тирам — та же математика, что при создании промо
        const tierPositions = buildCrystalPositions(
            selectedCategory.presets,
            getCrystalCategoryBudget(selectedCategory),
            getCrystalCategoryPlayers(selectedCategory)
        );
        return (
            <div className="w-full border-t border-white/20 mt-1 h-[100px] p-[16px] flex items-center justify-center gap-[8.5px]">
                {[...selectedCategory.presets].reverse().map((preset, reverseIndex) => {
                    const originalIndex = selectedCategory.presets.length - 1 - reverseIndex;
                    return (
                        <Crystal
                            key={originalIndex}
                            position={originalIndex + 1}
                            count={tierPositions[originalIndex]?.budget ?? preset.win}
                            src={positionIcons[originalIndex]}
                            onClick={() => setActivePresetModal({
                                preset,
                                iconId: originalIndex,
                                reversedOrder: true,
                            })}
                        />
                    );
                })}
            </div>
        );
    }, [selectedCategory, positionIcons, setActivePresetModal]);

    // Мемоизированный Promoter
    const promoterElement = useMemo(() => (
        <div className="w-[46px] h-[44px] flex items-center gap-[15px]">
            <div className="w-[44px] h-[44px] rounded-full relative">
                <img
                    width={44} height={44}
                    src={getPromoterAvatarSrc(link, data)}
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getPromoterAvatarSrc(link || data?.link, { ...data, logo: null, logoPath: null });
                    }}
                    className="border border-[#3B3836] rounded-full object-cover w-[44px] h-[44px]"
                />
                <img
                    width={16} height={16}
                    src={getIcon('octo')}
                    className="absolute bottom-0 right-0 rounded-full"
                />
            </div>
        </div>
    ), [link, data]);

    const costContent = useMemo(() => {
        let costDisplay;
        if (feeLoading) {
            costDisplay = '...';
        } else if (fee != null) {
            costDisplay = `$${fee}`;
        } else {
            costDisplay = '—';
        }

        if (isBonusMode && fee != null) {
            return (
                <div className="flex items-center gap-1">
                    <span className="line-through text-[12px] text-gray-400">${fee}</span>
                    <span
                        className="text-[12px]"
                        style={{
                            fontFamily: 'Anton, sans-serif',
                            background: 'linear-gradient(90deg, #DC1FFF 0%, #3A88F7 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        FREE
                    </span>
                </div>
            );
        }

        return <span>{costDisplay}</span>;
    }, [fee, feeLoading, isBonusMode]);

    // Мемоизированный Participants
    const participantsElement = useMemo(() => {
        if (!selectedCategory) return null;

        const participantsCount = getCrystalCategoryPlayers(selectedCategory);
        const winnersCount = participantsCount;
        const participantsPct = 100;
        const winnersPct = 100;
        const nonWinnersCount = 0;
        const nonWinnersPct = 0;

        return (
            <div className="w-full flex justify-between px-4">
                <ParticipantsValueRect
                    title="players"
                    count={participantsCount}
                    percentage={participantsPct}
                />
                <ParticipantsValueRect
                    title="winners"
                    count={winnersCount}
                    percentage={winnersPct}
                />
                <div className="w-[84px] flex flex-col items-center justify-center gap-1">
                    <span className="text-[#D8D8D8] text-[12px] ">non-winner</span>
                    <div className="w-full h-6 flex items-center justify-center font-roboto text-[14px] rounded-[40px] border border-white/15  text-white">
                        {nonWinnersCount}
                    </div>
                    <span className="text-[#959595] text-[12px] font-roboto">
                        {nonWinnersPct ? `${nonWinnersPct}%` : '\u00A0'}
                    </span>
                </div>
            </div>
        );
    }, [selectedCategory]);

    // Мемоизированный CoinBadge
    const coinBadgeElement = useMemo(() => {
        if (!selectedCategory) {
            return <CoinBadge fixedWidth={140} isCrystal icon="diamond"  count={0} iconHeight={24} iconWidth={24} />;
        }
        const gem = gemTypes.find(g => g.id === selectedCategory.gemId);
        return (
            <CoinBadge 
                fixedWidth={130}
                isCrystal
                token={gem?.frontImage ? IMAGE_BASE_URL + gem.frontImage : undefined}
                icon={!gem?.frontImage ? "diamond" : undefined}
                isGiveaway 
                count={getCrystalCategoryBudget(selectedCategory)} 
                iconHeight={24} 
                iconWidth={24} 
            />
        );
    }, [selectedCategory, gemTypes]);

    const prizePoolCount = selectedCategory
        ? getCrystalCategoryBudget(selectedCategory)
        : 0;

    const prizePoolGemName = useMemo(() => {
        if (!selectedCategory) return 'Crystals';
        const gem = gemTypes.find(g => g.id === selectedCategory.gemId);
        return gem?.name || 'Crystals';
    }, [selectedCategory, gemTypes]);

    // Итоговый JSX
    return (
        <>
        <div className="w-full flex flex-col ">
            <div className='w-full flex flex-col items-center rounded-[8px] border border-white/20'>
                <div className="w-full px-3 pt-3 pb-1">
                    <div className='w-full flex items-center justify-center mb-2'>
                        <span className="text-[14px] font-semibold text-[#D8D8D8]">Promo by</span>
                    </div>
                    {/* <Time /> */}
                    <div className="w-full flex items-center justify-between gap-[12px]">
                        <div className="relative" ref={promoterRef}>
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowPromoterTooltip(v => !v)}
                            >
                                {promoterElement}
                            </div>

                            <AnimatePresence>
                                {showPromoterTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.25, ease: 'easeOut' }}
                                        className={`min-w-[280px] ${hasResolvedPromoterLink ? 'h-32' : 'h-24'} absolute top-[calc(100%+10px)] -left-2 z-10 bg-[#2373BE] rounded-[10px]`}
                                    >
                                        <div className="w-full h-full relative">
                                            <div className="w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[7px] left-[22px]" />
                                            <div className="w-full h-full flex flex-col items-start p-3 gap-1">
                                                <span className="text-white text-[14px] font-semibold">Promo Creator Name</span>
                                                {hasResolvedPromoterLink && (
                                                    <span className="text-white text-[12px] font-semibold">
                                                        {getPromoterDisplayName(data) || data?.name}
                                                    </span>
                                                )}
                                                <span className="text-white/80 text-[13px] truncate max-w-full">Promoter TG link</span>
                                                {hasResolvedPromoterLink && (
                                                    <span className="text-white/80 text-[11px] truncate max-w-full">{link}</span>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="flex flex-col gap-[4px] items-start overflow-hidden w-full">
                            <div
                                className={`w-full h-[25px] rounded-[4.5px] bg-white/15 flex items-center justify-center relative gap-[6px] py-[3px] pl-3 pr-7 overflow-hidden cursor-pointer ${tgLoading ? 'opacity-50' : ''}`}
                                onClick={handleOpenKeyboard}
                            >
                                {!hasResolvedPromoterLink && (
                                    <div className="absolute left-[8px] top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#FB2C36] shadow-[0_0_6px_#FB2C36] flex-shrink-0" />
                                )}
                                <img
                                    width={12} height={12}
                                    src={getIcon('link')}
                                    className=" flex-shrink-0 absolute right-[9.5px]"
                                />
                                <span className={`${hasResolvedPromoterLink ? 'text-white/90 text-[14px]' : 'text-[#959595] text-[14px]'} truncate flex-1 text-center`}>
                                    {promoterLinkDisplay}
                                </span>
                            </div>
                        </div>

                        <Time days={selectedCategory?.number ?? 7} />
                    </div>
                    <div className='w-full flex items-center justify-between pt-2'>
                        <div className='w-[50px] h-[24px] border-[2px] border-[#353232] rounded-[6px] flex flex-row items-center justify-center'>
                            <span className='text-[12px] text-[#D8D8D8]'>{(selectedCategory?.name ?? '')}</span>
                        </div>

                        <div className="relative" ref={prizePoolRef}>
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowPrizePoolTooltip(v => !v)}
                            >
                                {coinBadgeElement}
                            </div>

                            <AnimatePresence>
                                {showPrizePoolTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.25, ease: 'easeOut' }}
                                        className="absolute top-[calc(100%+10px)] -left-7 min-w-[220px] min-h-20 bg-[#2373BE] z-10 rounded-[10px]"
                                    >
                                        <div className="w-full h-full flex flex-col items-center relative">
                                            <div className="w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[6.5px] left-0 right-0 m-auto" />
                                            <div className="w-full flex flex-col gap-1 p-3 text-center">
                                                <span className="text-white text-[12px] font-semibold">Giveaway Prize Pool</span>
                                                <span className="text-white/80 text-[12px]">shared between all winners</span>
                                                <div className="w-full flex items-center justify-center">
                                                    <span className="text-white text-[11px]">
                                                        {prizePoolCount} crystals
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="w-[64px] flex flex-col items-center justify-center gap-1">
                            <div className={`w-full h-6 flex items-center justify-center font-roboto text-[12px] font-bold rounded-[40px] border border-white/15
                                ${isBonusMode ? 'bg-gradient-to-r from-[#DC1FFF]/20 to-[#3A88F7]/20 text-white' : 'bg-[#D3D3D3] text-black'}`}>
                                {costContent}
                            </div>
                        </div>
                        
                    </div>
                </div>
                {crystalsLine}
            </div>
            <div className='w-full h-20 flex flex-col items-center justify-center border border-white/20 rounded-[8px] mt-2'>
                {participantsElement}
            </div>

            <AnimatePresence>
                {activePresetModal && (
                    <CrystalDetailModal
                        modalData={activePresetModal}
                        onClose={() => setActivePresetModal(null)}
                    />
                )}
            </AnimatePresence>

        </div>
        <PromoSnackbar
            message={snackbar.message}
            type={snackbar.type}
            isVisible={snackbar.isVisible}
            isAnimated={snackbarAnimation}
            onClose={closeSnackbar}
        />
        </>
    );
}
